import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Avatar,
    Box,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Toolbar,
    Tooltip,
    alpha,
    useTheme,
} from '@mui/material';
import { Help, Menu as MenuIcon, Person, Search } from '@mui/icons-material';
import { ZendeskAPI } from 'react-zendesk';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services';
import { setSearch } from 'store/features/globalSearch';
import { getEstimatedTankLevel } from 'store/services/device-predictions';
import { Device } from 'types';
import { useAuth, useGlobalSearch, useLogout } from 'hooks';
import { useUi } from 'hooks/useUi';
import { TopSearchBar } from './TopSearchBar/TopSearchBar';
import { TankLevelIndicator } from 'components/data/TankLevelIndicator/TankLevelIndicator';
import styles from './TopStatusBar.module.scss';
import { MobileBackButton } from 'components/common/MobileBackButton/MobileBackButton';
import { useViewAsSupplierSelector } from 'store/features/viewAsSupplier';
import { Logo } from 'lib/themes';

export interface TopStatusBarProps {
    onMenuToggle: () => void;
}

export default function TopStatusBar({ onMenuToggle }: TopStatusBarProps) {
    const dispatch = useDispatch();
    const location = useLocation();
    const auth = useAuth();
    const navigate = useNavigate();
    const logout = useLogout();
    const { ui } = useUi();
    const theme = useTheme();

    const [query, { data, isLoading, isFetching }] =
        useLazyGetSupplierDevicesEsQuery();
    const { globalSearchTerm } = useGlobalSearch();
    const { viewAsSupplier } = useSelector(useViewAsSupplierSelector);

    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false);
    const [isSearchResultsOpen, setIsSearchResultsOpen] =
        useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>(
        globalSearchTerm ?? ''
    );
    const [isSearchMenuFocused, setIsSearchMenuFocused] =
        useState<boolean>(false);
    const [showBackMobileButton, setShowMobileBackButton] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

    const userSettingsRef = useRef<HTMLButtonElement>(null);
    const searchBarRef = useRef<HTMLDivElement>(null);

    const getInitials = () => {
        let output = '';
        if (!auth.user || !auth.user.displayName) return output;
        output += auth.user.displayName[0];
        const spacePosition = auth.user.displayName.indexOf(' ');
        if (
            spacePosition === -1 ||
            spacePosition >= auth.user.displayName.length - 1
        )
            return output;
        output += auth.user.displayName[spacePosition + 1];
        return output;
    };

    const openHelpDialog = () => {
        ZendeskAPI('webWidget', 'identify', {
            name: auth.user?.displayName ?? auth.user?.email,
            email: auth.user?.email,
            organization: auth.user?.protected?.supplierId,
        });
        ZendeskAPI('webWidget', 'prefill', {
            email: {
                value: auth.user?.email,
                readOnly: true,
            },
        });
        ZendeskAPI('webWidget', 'show');
        ZendeskAPI('webWidget', 'open');
    };

    const searchTermChanged = (term: string) => {
        setSearchTerm(term);
    };

    // If the globalSearchTerm is updated from outside of the search bar component (e.g., when the user clears the keyword search filter chip)
    // make sure that the search bar text input field clears to match that.
    useEffect(() => {
        setSearchTerm(globalSearchTerm);
    }, [globalSearchTerm]);

    // Based on current page, determine whether or not a back button should be shown on mobile.
    useEffect(() => {
        const showBackButtonForPages = ['device-details'];
        if (location.pathname) {
            setShowMobileBackButton(false);
            const pagePaths = location.pathname.split('/');
            pagePaths.forEach((pagePath: string) => {
                if (showBackButtonForPages.includes(pagePath))
                    setShowMobileBackButton(true);
            });
        }
    }, [location]);

    const onSearch = (term: string) => {
        if (term === '' || term === null || term === undefined) {
            setIsSearchResultsOpen(false);
            // next line is temporary until we have a filter chip for the global search
            dispatch(setSearch(''));
            return;
        }
        setIsSearchResultsOpen(true);
        query({
            userUid: auth.user?.uid ?? '',
            supplierId:
                viewAsSupplier ||
                auth.user?.protected?.supplierId?.toString() ||
                '',
            queryConfig: {
                limit: ui.isMobile ? 5 : 10,
                offset: 0,
                direction: 'DESC',
                sortField: 'lastReading.time',
                search: term,
                isAdminUser:
                    auth.user?.protected?.supplierId?.toString() === '0',
            },
        });
    };
    const onSearchBarArrowKey = (_direction: 'up' | 'down') => {
        if (isLoading || isFetching) return;
        if (!data || !data.results || data.results.length === 0) return;
        setIsSearchResultsOpen(true);
        setIsSearchMenuFocused(true);
    };
    const onSearchBarEnterKey = () => {
        if (isLoading || isFetching) return;
        filterBasedOnSearch();
        setIsSearchResultsOpen(false);
        setIsSearchMenuFocused(false);
    };
    const onSearchMenuClosed = () => {
        setIsSearchResultsOpen(false);
        setIsSearchMenuFocused(false);
    };
    const searchResultClicked = (device: Device) => {
        navigate(`/device-details/${device.deviceId}`);
        onSearchMenuClosed();
    };
    const filterBasedOnSearch = () => {
        dispatch(setSearch(searchTerm));
        onSearchMenuClosed();
    };

    const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onMenuToggle();
    };

    return (
        <AppBar
            style={{ backgroundImage: 'none' }}
            className={styles.TopStatusBar}
            data-testid="TopStatusBar"
        >
            <Toolbar variant="dense">
                {ui.isMobile && (
                    <>
                        {showBackMobileButton ? (
                            <MobileBackButton />
                        ) : (
                            <IconButton
                                onClick={handleMenuClick}
                                sx={{ color: '#fafafa' }}
                            >
                                <MenuIcon fontSize="large" />
                            </IconButton>
                        )}
                    </>
                )}
                {!ui.isMobile && (
                    <IconButton
                        onClick={() => navigate('/live-reporting')}
                        className={styles.logo}
                    >
                        <Logo height="50" />
                    </IconButton>
                )}
                <div
                    ref={searchBarRef}
                    className={
                        isMobileSearchOpen ? styles.mobileSearchOpen : ''
                    }
                >
                    <TopSearchBar
                        onSearch={onSearch}
                        onSearchTermChange={searchTermChanged}
                        onArrowKey={onSearchBarArrowKey}
                        searchTerm={searchTerm}
                        onEnterKey={onSearchBarEnterKey}
                        onMobileSearchOpen={setIsMobileSearchOpen}
                    />
                </div>
                <Menu
                    keepMounted
                    open={isSearchResultsOpen}
                    onClose={onSearchMenuClosed}
                    disableAutoFocus={true}
                    autoFocus={isSearchMenuFocused}
                    anchorEl={searchBarRef.current}
                >
                    {(isLoading || isFetching) &&
                        [...Array(10)].map((_e, i) => (
                            <MenuItem key={i}>
                                <Skeleton sx={{ width: '437px' }} />
                            </MenuItem>
                        ))}
                    {!isLoading &&
                        !isFetching &&
                        !!data?.results &&
                        data?.results.length > 0 && (
                            <MenuItem
                                onClick={filterBasedOnSearch}
                                sx={{
                                    borderBottom: `1px ${alpha(
                                        theme.palette.text.secondary,
                                        0.25
                                    )} solid`,
                                }}
                            >
                                <Search />
                                Filter based on this search
                            </MenuItem>
                        )}
                    {!isLoading &&
                        !isFetching &&
                        data?.results.map((device, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => searchResultClicked(device)}
                            >
                                <ListItemIcon>
                                    <TankLevelIndicator
                                        tankLevelPercentage={getEstimatedTankLevel(
                                            device.lastReading,
                                            device.lastPrediction
                                        )}
                                        gallons={device.gallons}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        device.customer_name ??
                                        device.name ??
                                        device.deviceId
                                    }
                                    secondary={device.address}
                                    sx={{ marginLeft: '10px' }}
                                />
                            </MenuItem>
                        ))}
                    {!isLoading &&
                        !isFetching &&
                        data?.results.length === 0 && (
                            <MenuItem onClick={onSearchMenuClosed}>
                                No results found.
                            </MenuItem>
                        )}
                </Menu>

                {!isMobileSearchOpen ? (
                    <>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            aria-label="help"
                            color="info"
                            className={styles.helpBadge}
                            onClick={openHelpDialog}
                            startIcon={<Help />}
                            style={{ color: theme.palette.primary.main }}
                        >
                            <div className={styles.helpText}>Help</div>
                        </Button>
                        <Tooltip title="Open settings">
                            <IconButton
                                ref={userSettingsRef}
                                onClick={() => setIsProfileMenuOpen(true)}
                            >
                                <Avatar
                                    sx={{
                                        height: '33px',
                                        width: '33px',
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        fontSize: 16,
                                    }}
                                >
                                    {getInitials() || <Person />}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu
                            keepMounted
                            open={isProfileMenuOpen}
                            anchorEl={userSettingsRef.current}
                            onClose={() => setIsProfileMenuOpen(false)}
                        >
                            <MenuItem>
                                <NavLink
                                    to="/profile"
                                    className={styles.unstyledMenuLink}
                                    onClick={() => setIsProfileMenuOpen(false)}
                                >
                                    Profile
                                </NavLink>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setIsProfileMenuOpen(false);
                                    logout();
                                }}
                            >
                                Logout
                            </MenuItem>
                        </Menu>
                    </>
                ) : null}
            </Toolbar>
        </AppBar>
    );
}
